import { Check, Flame, Star, Building2, User, Bot } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { PLANS, type Plan, type PlanCategory } from "@/lib/plans";
import { useI18n } from "@/lib/i18n";

function FoundingBanner() {
  return (
    <div className="mb-10 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl border border-orange-500/30 bg-orange-500/8 px-6 py-4 text-sm">
      <div className="flex items-center gap-2 font-semibold text-orange-400">
        <Flame className="w-4 h-4" />
        Founding Member Pricing — First 100 total members only
      </div>
      <div className="h-4 w-px bg-orange-500/30 hidden sm:block" />
      <div className="font-semibold text-white">
        Lock In Your Founding Price
      </div>
    </div>
  );
}

const AUDIENCE_STORAGE_KEY = "adkavi_pricing_audience";

const AI_AGENT_BY_PLAN: Record<string, string> = {
  starter_monthly: "AI Lead Agent — Facebook DMs only",
  growth_monthly: "AI Lead Agent — Instagram + Facebook DMs",
  growth_ai_monthly: "AI Lead Agent — WhatsApp + Instagram + Facebook DMs",
  agency_starter_monthly: "AI Lead Agent — Facebook DMs only (per client)",
  agency_growth_monthly: "AI Lead Agent — Instagram + Facebook DMs (per client)",
  agency_pro_video_monthly: "AI Lead Agent — WhatsApp + Instagram + Facebook DMs (per client)",
};

function Card({ plan }: { plan: Plan }) {
  const { t } = useI18n();
  const popular = plan.popular;
  return (
    <div
      className={`relative p-6 md:p-7 rounded-3xl border flex flex-col ${
        popular
          ? "bg-gradient-to-br from-primary/15 to-secondary/10 border-primary glow-orange"
          : "bg-card border-border"
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          <Star className="w-3 h-3" fill="currentColor" /> {t("pricing.popular")} · Founding Member Spot
        </div>
      )}
      {plan.foundingOffer && !popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-card border border-primary/50 text-primary text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
          Founding Member Spot
        </div>
      )}
      <h3 className="font-bold text-xl mb-1">{plan.name}</h3>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-4xl font-bold">${plan.price.toLocaleString()}</span>
        <span className="text-sm text-muted-foreground">{t("pricing.perMonth")}</span>
        {plan.originalPrice && (
          <span className="text-sm text-muted-foreground line-through">${plan.originalPrice.toLocaleString()}</span>
        )}
      </div>
      {plan.foundingOffer && (
        <p className="text-xs text-orange-400 font-medium mb-2">
          Limited to the first 100 total founding members
        </p>
      )}
      <p className="text-sm font-medium text-foreground">{plan.accountLine}</p>
      <p className="text-sm text-muted-foreground mb-5">{plan.platforms}</p>

      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.categories.map((item) => (
          <li key={`cat-${item}`} className="flex gap-2 text-sm">
            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="uppercase text-foreground">{t(`plan.cat.${item}`)}</span>
          </li>
        ))}
        {plan.meta.map((item) => (
          <li key={`meta-${item}`} className="flex gap-2 text-sm">
            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="uppercase text-foreground">{t(`plan.meta.${item}`)}</span>
          </li>
        ))}
        {AI_AGENT_BY_PLAN[plan.priceId] && (
          <li className="flex gap-2 text-sm">
            <Bot className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span className="uppercase text-foreground">{AI_AGENT_BY_PLAN[plan.priceId]}</span>
          </li>
        )}
      </ul>

      <WaitlistDialog plan={{ name: plan.name, price: plan.price }}>
        <button
          style={{ backgroundColor: "#e8721a", color: "#ffffff" }}
          className="block w-full text-center rounded-full min-h-[44px] py-3 text-base font-semibold hover:brightness-110 transition"
        >
          {t("pricing.startTrial")}
        </button>
      </WaitlistDialog>
    </div>
  );
}

export function Pricing({ audience = "creator", showAudienceGate = false }: { audience?: PlanCategory; showAudienceGate?: boolean } = {}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [gateChoice, setGateChoice] = useState<PlanCategory | null>(
    showAudienceGate ? null : audience,
  );

  useEffect(() => {
    if (!showAudienceGate) return;
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(AUDIENCE_STORAGE_KEY) as PlanCategory | null;
    if (stored === "creator") {
      setGateChoice("creator");
    } else if (stored === "agency") {
      // Honor saved choice but stay on this page; user can still re-pick.
      setGateChoice("creator");
    }
  }, [showAudienceGate]);

  const handleChoose = (choice: PlanCategory) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(AUDIENCE_STORAGE_KEY, choice);
    }
    if (choice === "agency") {
      navigate({ to: "/agencies" });
      return;
    }
    setGateChoice("creator");
  };

  const effectiveAudience: PlanCategory = showAudienceGate ? (gateChoice ?? "creator") : audience;
  const plans = PLANS.filter((p) => p.category === effectiveAudience);
  const isAgency = effectiveAudience === "agency";
  const showGate = showAudienceGate && gateChoice === null;

  return (
    <section id="pricing" className="py-20 md:py-24 px-4 md:px-6 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            {t("pricing.title.1")}{" "}
            <span className="text-gradient-orange">{t("pricing.title.2")}</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            {t("pricing.subtitle")}
          </p>
        </div>

        <FoundingBanner />

        {showGate ? (
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-8">{t("pricing.gate.title")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              <button
                onClick={() => handleChoose("creator")}
                className="group p-8 rounded-3xl border border-border bg-card hover:border-primary hover:bg-primary/5 transition text-left"
              >
                <User className="w-8 h-8 text-primary mb-4" />
                <div className="font-bold text-lg mb-1">{t("pricing.gate.creator.title")}</div>
                <p className="text-sm text-muted-foreground">
                  {t("pricing.gate.creator.desc")}
                </p>
              </button>
              <button
                onClick={() => handleChoose("agency")}
                className="group p-8 rounded-3xl border border-border bg-card hover:border-primary hover:bg-primary/5 transition text-left"
              >
                <Building2 className="w-8 h-8 text-primary mb-4" />
                <div className="font-bold text-lg mb-1">{t("pricing.gate.agency.title")}</div>
                <p className="text-sm text-muted-foreground">
                  {t("pricing.gate.agency.desc")}
                </p>
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className={`text-sm uppercase tracking-[0.2em] mb-6 text-center ${isAgency ? "text-secondary" : "text-muted-foreground"}`}>
              {isAgency ? t("pricing.forAgencies") : t("pricing.forCreators")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
              {plans.map((p) => (
                <Card key={p.priceId} plan={p} />
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground mt-6 max-w-2xl mx-auto">
              {t("pricing.adSpendNote")}
            </p>

            <div className="text-center mt-10">
              {isAgency ? (
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition">
                  {t("pricing.viewIndividual")}
                </Link>
              ) : (
                <Link to="/agencies" className="text-sm text-muted-foreground hover:text-foreground transition">
                  {t("pricing.viewAgency")}
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
